import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './Tooltip.css'; // Import the CSS for the tooltip

const Tooltip = ({ children, text, position = 'top', variant, className = '', hideOnPress = false, keepPosition = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState(position);
  const targetRef = useRef(null);
  const tooltipRef = useRef(null);

  const hideTooltip = useCallback(() => {
    setShowTooltip(false);
    setActualPosition(position);
  }, [position]);

  // Browsers often skip mouseleave when the page scrolls but the pointer does not move,
  // or when switching tabs — the tooltip would otherwise stay visible indefinitely.
  useEffect(() => {
    if (!showTooltip) return undefined;

    const onScroll = () => hideTooltip();
    const onBlur = () => hideTooltip();
    const onVisibility = () => {
      if (document.hidden) hideTooltip();
    };
    const onPointerDownCapture = (e) => {
      if (targetRef.current && !targetRef.current.contains(e.target)) {
        hideTooltip();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') hideTooltip();
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showTooltip, hideTooltip]);

  useEffect(() => {
    if (showTooltip && targetRef.current && tooltipRef.current) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      let top = 0, left = 0, pos = position;
      const margin = 8;
      // Calculate position
      if (position === 'top') {
        top = targetRect.top - tooltipRect.height - margin;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        if (!keepPosition && top < 0) pos = 'bottom';
      }
      if (position === 'bottom' || pos === 'bottom') {
        top = targetRect.bottom + margin;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        if (!keepPosition && top + tooltipRect.height > window.innerHeight) pos = 'top';
      }
      if (position === 'left') {
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.left - tooltipRect.width - margin;
        if (!keepPosition && left < 0) pos = 'right';
      }
      if (position === 'right' || pos === 'right') {
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.right + margin;
        if (!keepPosition && left + tooltipRect.width > window.innerWidth) pos = 'left';
      }
      if (pos === 'top' && (position === 'bottom' || actualPosition === 'bottom')) {
        top = targetRect.top - tooltipRect.height - margin;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
      }
      if (pos !== actualPosition) {
        setActualPosition(pos);
      }
      // Clamp to viewport
      top = Math.max(0, Math.min(top, window.innerHeight - tooltipRect.height));
      left = Math.max(0, Math.min(left, window.innerWidth - tooltipRect.width));
      setCoords({ top, left });
    }
    // eslint-disable-next-line
  }, [showTooltip, position, actualPosition, text, keepPosition]);

  const isStringContent = typeof text === 'string' || typeof text === 'number';
  const tooltipText = isStringContent ? String(text).trim() : '';
  const hasTooltipContent = isStringContent ? !!tooltipText : text != null && text !== false;
  if (!hasTooltipContent) {
    return children;
  }

  const tooltipVariantClass = variant === 'synopsis' ? ' tooltip--synopsis' : '';
  const containerClass = [
    'tooltip-container',
    variant === 'synopsis' ? 'tooltip-container--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Portal tooltip
  const tooltipNode = showTooltip ? ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className={`tooltip tooltip-portal tooltip-${actualPosition}${tooltipVariantClass}`}
      style={{ top: coords.top, left: coords.left, position: 'fixed', zIndex: 9999, visibility: showTooltip ? 'visible' : 'hidden', opacity: showTooltip ? 1 : 0 }}
    >
      {isStringContent ? tooltipText : text}
    </div>,
    document.body
  ) : null;

  const Wrapper = variant === 'synopsis' ? 'div' : 'span';

  return (
    <Wrapper
      ref={targetRef}
      className={containerClass}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={hideTooltip}
      onPointerLeave={hideTooltip}
      onPointerDown={hideOnPress ? hideTooltip : undefined}
    >
      {children}
      {tooltipNode}
    </Wrapper>
  );
};

export default Tooltip;

// Present tooltip has limitations for long text near viewport edges (overlap / jitter).